import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { FormField } from '../components/FormField';
import { TextInput } from '../components/TextInput';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { useAuth } from '../auth/context';
import { usePostAuthRedirect } from '../org/usePostAuthRedirect';
import { toDisplayError, toFieldErrors, REGISTER_CONFLICT_MESSAGE } from '../api/errorMessages';
import { isApiError } from '../api/errors';
import styles from './SignUp.module.css';

interface FieldErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
}

interface FormSummary {
  message: string;
  requestId?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD_LENGTH = 12;

/** Client-side validation for fast feedback; the server remains authoritative on
 * password strength and breach checks (returned as a 422 field error). */
function validate(email: string, password: string, confirmPassword: string): FieldErrors {
  const errors: FieldErrors = {};
  if (!email.trim()) {
    errors.email = 'Enter your email address.';
  } else if (!EMAIL_RE.test(email)) {
    errors.email = 'Enter a valid email address.';
  }
  if (!password) {
    errors.password = 'Enter a password.';
  } else if (password.length < MIN_PASSWORD_LENGTH) {
    errors.password = 'Use at least 12 characters.';
  }
  if (!confirmPassword) {
    errors.confirmPassword = 'Re-enter your password.';
  } else if (password !== confirmPassword) {
    errors.confirmPassword = 'Passwords do not match.';
  }
  return errors;
}

export function SignUp() {
  const { register } = useAuth();
  const resolvePostAuth = usePostAuthRedirect();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [summary, setSummary] = useState<FormSummary | null>(null);
  const [busy, setBusy] = useState(false);

  const summaryRef = useRef<HTMLDivElement>(null);

  // Focus the error summary after it commits (deterministic vs rAF).
  useEffect(() => {
    if (summary) summaryRef.current?.focus();
  }, [summary]);

  const focusSummary = () => {
    requestAnimationFrame(() => summaryRef.current?.focus());
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;

    const errors = validate(email, password, confirmPassword);
    if (errors.email || errors.password || errors.confirmPassword) {
      setFieldErrors(errors);
      setSummary({ message: 'Please fix the errors below and try again.' });
      focusSummary();
      return;
    }

    setFieldErrors({});
    setSummary(null);
    setBusy(true);
    try {
      await register(email, password);
      const dest = await resolvePostAuth();
      navigate(dest, { replace: true });
    } catch (err) {
      // A 422 with a per-field discriminator maps to the password field (weak or
      // breached password). Everything else stays form-level.
      const fields = toFieldErrors(err);
      if (fields.password) {
        setFieldErrors({ password: fields.password });
        setSummary({ message: 'Please fix the errors below and try again.' });
      } else if (isApiError(err) && err.status === 409) {
        // Duplicate email — generic form-level copy (never on the email field, to
        // avoid confirming an account exists).
        setSummary({ message: REGISTER_CONFLICT_MESSAGE });
      } else {
        const display = toDisplayError(err);
        setSummary({
          message: display.message,
          ...(display.requestId ? { requestId: display.requestId } : {}),
        });
      }
      focusSummary();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell variant="centered">
      <Card>
        <form className={styles.form} onSubmit={onSubmit} noValidate>
          <h1 className={styles.heading}>Create your account</h1>
          <p className={styles.subheading}>Start using TestCraft Journeys.</p>

          <div className={styles.summary}>
            <InlineAlert
              ref={summaryRef}
              severity="error"
              {...(summary?.requestId ? { requestId: summary.requestId } : {})}
            >
              {summary?.message}
            </InlineAlert>
          </div>

          <FormField
            label="Email"
            id="email"
            {...(fieldErrors.email ? { error: fieldErrors.email } : {})}
          >
            {({ controlProps }) => (
              <TextInput
                {...controlProps}
                type="email"
                name="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </FormField>

          <FormField
            label="Password"
            id="password"
            helpText="At least 12 characters."
            {...(fieldErrors.password ? { error: fieldErrors.password } : {})}
          >
            {({ controlProps }) => (
              <TextInput
                {...controlProps}
                type="password"
                name="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </FormField>

          <FormField
            label="Confirm password"
            id="confirm-password"
            {...(fieldErrors.confirmPassword ? { error: fieldErrors.confirmPassword } : {})}
          >
            {({ controlProps }) => (
              <TextInput
                {...controlProps}
                type="password"
                name="confirm-password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            )}
          </FormField>

          <div className={styles.actions}>
            <Button type="submit" busy={busy} fullWidth>
              {busy ? 'Creating account…' : 'Create account'}
            </Button>
          </div>

          <p className={styles.link}>
            <Link to="/sign-in">Already have an account? Sign in</Link>
          </p>
        </form>
      </Card>
    </AppShell>
  );
}
